import {
  FlashOn as EnergyIcon,
  AccountTree as JourneyIcon,
  LocationOn as LocationIcon,
  Login as LoginIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  MyCustomerApiResponse,
  WorkItemAction,
} from "../../../store/services/customerService";
import {
  getWorkItemColor,
  getWorkItemTypeName,
} from "../../../utils/workItemUtils";
import WorkItemListItem from "../../workitem/WorkItemListItem";
import CustomerContextMenu, { CustomerAction } from "./CustomerContextMenu";
import { getMostRecentLogin } from "./dateUtils";
import { formatTime, getChipColor } from "./helpers";

interface CustomerCardRowProps {
  customers: MyCustomerApiResponse[];
  onCustomerClick: (customerId: number) => void;
  onContextMenu?: (
    customer: MyCustomerApiResponse,
    event: React.MouseEvent
  ) => void;
  selectedRows: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  onBulkSegmentUpdate: () => void;
}

const formatConsumption = (consumption: number | null) => {
  if (consumption === null) return "--";
  return `${consumption.toLocaleString("sv-SE")} kWh`;
};

const CustomerCardRow: React.FC<CustomerCardRowProps> = ({
  customers,
  onCustomerClick,
  onContextMenu,
  selectedRows,
  onSelectionChange,
}) => {
  const theme = useTheme();
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    customer: MyCustomerApiResponse;
  } | null>(null);

  const [shiftSelectStartIndex, setShiftSelectStartIndex] = useState<
    number | null
  >(null);
  const [isMultiSelecting, setIsMultiSelecting] = useState(false);

  const handleCheckboxClick = (
    event: React.MouseEvent,
    customerId: number,
    index: number
  ) => {
    event.stopPropagation();

    if (event.shiftKey && shiftSelectStartIndex !== null) {
      const startIdx = Math.min(shiftSelectStartIndex, index);
      const endIdx = Math.max(shiftSelectStartIndex, index);

      const rangeIds = customers
        .slice(startIdx, endIdx + 1)
        .map((customer) => customer.Id);

      if (event.ctrlKey || event.metaKey) {
        onSelectionChange(Array.from(new Set([...selectedRows, ...rangeIds])));
      } else {
        onSelectionChange(rangeIds);
      }

      setIsMultiSelecting(true);
    } else if (event.ctrlKey || event.metaKey) {
      if (selectedRows.includes(customerId)) {
        onSelectionChange(selectedRows.filter((id) => id !== customerId));
      } else {
        onSelectionChange([...selectedRows, customerId]);
      }

      setShiftSelectStartIndex(index);
      setIsMultiSelecting(true);
    } else {
      if (selectedRows.length === 1 && selectedRows[0] === customerId) {
        onSelectionChange([]);
        setShiftSelectStartIndex(null);
        setIsMultiSelecting(false);
      } else {
        onSelectionChange([customerId]);
        setShiftSelectStartIndex(index);
        setIsMultiSelecting(true);
      }
    }
  };

  const handleRowClick = (
    customerId: number,
    index: number,
    event: React.MouseEvent
  ) => {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      handleCheckboxClick(event, customerId, index);
    } else if (isMultiSelecting) {
      onSelectionChange([customerId]);
      setShiftSelectStartIndex(index);
    } else {
      onCustomerClick(customerId);
    }
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    customer: MyCustomerApiResponse
  ) => {
    event.preventDefault();

    if (onContextMenu) {
      onContextMenu(customer, event);
      return;
    }

    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      customer,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const isRowSelected = (customerId: number) => {
    return selectedRows.includes(customerId);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (isMultiSelecting && selectedRows.length === 0) {
        setIsMultiSelecting(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isMultiSelecting, selectedRows]);

  const handleMenuAction = (action: CustomerAction) => {
    const customerNo = contextMenu?.customer.Id;
    if (!customerNo) return;

    switch (action) {
      case "viewDetails":
        onCustomerClick(customerNo);
        break;
      case "copyId": {
        const customer = customers.find((c) => c.Id === customerNo);
        if (customer) {
          navigator.clipboard.writeText(customer.CustomerId);
        }
        break;
      }
      case "copyName": {
        const customer = customers.find((c) => c.Id === customerNo);
        if (customer) {
          navigator.clipboard.writeText(customer.Name);
        }
        break;
      }
    }

    handleCloseContextMenu();
  };

  const createWorkItemPreview = (
    workItem: WorkItemAction,
    type: "lastContact" | "nextAction",
    customer: MyCustomerApiResponse
  ) => {
    if (
      !workItem ||
      workItem.ActionType === undefined ||
      workItem.ActionType === null
    )
      return null;

    const description =
      type === "lastContact"
        ? "Senaste genomförda kontakt"
        : "Nästa planerade aktivitet";

    return (
      <WorkItemListItem
        workItemType={workItem.ActionType}
        title={workItem.Text || "Ingen titel"}
        description={description}
        date={workItem.ActionDate ?? undefined}
        customerName={customer.Name}
        showAction={false}
        resultData={workItem.Result}
      />
    );
  };

  return (
    <Box>
      {customers.map((customer, index) => {
        const isSelected = isRowSelected(customer.Id);
        const mostRecentLogin = getMostRecentLogin(customer);
        const haveContacts = customer.Communications.find(x => x.ContactPersonNo !== null) !== undefined;
        return (
          <Box
            key={customer.Id}
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              bgcolor: "grey.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.300",
              mb: 1,
              cursor: "pointer",
              transition: "all 0.2s",

              "&:hover": {
                boxShadow: 2,
                bgcolor: theme.palette.action.hover,
              },
            }}
            onClick={(event) => handleRowClick(customer.Id, index, event)}
            onContextMenu={(event) => handleContextMenu(event, customer)}
          >
            {/* Floating chips at top */}

            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                zIndex: 1,
                flexWrap: "wrap",
              }}
            >
              {/* Segment chip */}
              {customer.CustomerSegmentName && (
                <Chip
                  label={customer.CustomerSegmentName}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "white",
                    bgcolor: getChipColor(customer.CustomerSegmentName, theme),
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    border: "none",
                    boxShadow: 1,
                    "& .MuiChip-label": {
                      px: 0.75,
                      py: 0.25,
                    },
                  }}
                />
              )}

              {/* Journey type chip */}
              {customer.B2bJourneyType && (
                <Chip
                  icon={
                    <JourneyIcon sx={{ fontSize: "0.8rem", color: "white" }} />
                  }
                  label={customer.B2bJourneyType}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "white",
                    bgcolor: "primary.main",
                    border: "none",
                    boxShadow: 1,
                    "& .MuiChip-label": {
                      px: 0.5,
                      py: 0.25,
                    },
                    "& .MuiChip-icon": {
                      color: "white",
                      fontSize: "0.8rem",
                    },
                  }}
                />
              )}

              {/* Customer ID chip */}
              {customer.CustomerId && (
                <Chip
                  label={customer.CustomerId}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "white",
                    bgcolor: "grey.700",
                    border: "none",
                    boxShadow: 1,
                    "& .MuiChip-label": {
                      px: 0.75,
                      py: 0.25,
                    },
                  }}
                />
              )}

              {/* Sales representative chip */}
              {customer.SalesResponisble && (
                <Chip
                  icon={
                    <PersonIcon sx={{ fontSize: "0.8rem", color: "white" }} />
                  }
                  label={customer.SalesResponisble}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "white",
                    bgcolor: "info.main",
                    border: "none",
                    boxShadow: 1,
                    "& .MuiChip-label": {
                      px: 0.5,
                      py: 0.25,
                    },
                    "& .MuiChip-icon": {
                      color: "white",
                      fontSize: "0.8rem",
                    },
                  }}
                />
              )}

              {/* Consumption chip */}
              {customer.ActiveFVolume !== null && (
                <Chip
                  icon={
                    <EnergyIcon sx={{ fontSize: "0.8rem", color: "white" }} />
                  }
                  label={formatConsumption(customer.ActiveFVolume)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "white",
                    bgcolor: "warning.main",
                    border: "none",
                    boxShadow: 1,
                    "& .MuiChip-label": {
                      px: 0.5,
                      py: 0.25,
                    },
                    "& .MuiChip-icon": {
                      color: "white",
                      fontSize: "0.8rem",
                    },
                  }}
                />
              )}
            </Box>
            {/* Right-aligned chips wrapper */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                zIndex: 1,
              }}
            >
              {!haveContacts && (
                <Tooltip title="Saknas kontaktperson" placement="left" arrow>
                  <Chip
                    icon={
                      <PersonIcon sx={{ fontSize: "0.8rem", color: "white" }} />
                    }
                    label={"Saknas kontaktperson"}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "white",
                      bgcolor: "red",
                      border: "none",
                      boxShadow: 1,
                      "& .MuiChip-label": {
                        px: 0.5,
                        py: 0.25,
                      },
                      "& .MuiChip-icon": {
                        color: "white",
                        fontSize: "0.8rem",
                      },
                    }}
                  />
                </Tooltip>
              )}
              {/* Last login chip */}
              {mostRecentLogin && (
                <Tooltip title="Senast inloggad" placement="left" arrow>
                  <Chip
                    icon={
                      <LoginIcon sx={{ fontSize: "0.8rem", color: "white" }} />
                    }
                    label={formatTime(mostRecentLogin)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "white",
                      bgcolor: "info.main",
                      border: "none",
                      boxShadow: 1,
                      "& .MuiChip-label": {
                        px: 0.5,
                        py: 0.25,
                      },
                      "& .MuiChip-icon": {
                        color: "white",
                        fontSize: "0.8rem",
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* Activities badges in bottom right corner */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                zIndex: 1,
              }}
            >
              {/* Last contact badge */}
              <Box
                sx={{
                  position: "relative",
                  borderTop: "1px solid",
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px 0 0 0",
                  bgcolor: "background.paper",
                  px: 1,
                  py: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: -8,
                    left: 8,
                    fontSize: "0.55rem",
                    color: "text.secondary",
                    bgcolor: "background.paper",
                    px: 0.5,
                  }}
                >
                  Senaste
                </Typography>
                {customer.LastAction?.ActionDate ? (
                  <Tooltip
                    title={createWorkItemPreview(
                      customer.LastAction,
                      "lastContact",
                      customer
                    )}
                    placement="top"
                    arrow
                    enterDelay={300}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "transparent",
                          boxShadow: "none",
                          maxWidth: "none",
                          "& .MuiTooltip-arrow": {
                            color: "transparent",
                          },
                        },
                      },
                    }}
                  >
                    <Chip
                      label={`${getWorkItemTypeName(
                        customer.LastAction.ActionType
                      )} ${formatTime(customer.LastAction.ActionDate)}`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.65rem",
                        bgcolor: getWorkItemColor(
                          customer.LastAction.ActionType
                        ),
                        color: "white",
                        fontWeight: 600,
                        "& .MuiChip-label": {
                          px: 0.75,
                        },
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.6rem",
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    Ingen kontakt
                  </Typography>
                )}
              </Box>

              {/* Next action badge */}
              <Box
                sx={{
                  position: "relative",
                  borderTop: "1px solid",
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px 0 0 0",
                  bgcolor: "background.paper",
                  px: 1,
                  py: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    top: -8,
                    left: 8,
                    fontSize: "0.55rem",
                    color: "text.secondary",
                    bgcolor: "background.paper",
                    px: 0.5,
                  }}
                >
                  Kommande
                </Typography>
                {customer.NextAction ? (
                  <Tooltip
                    title={createWorkItemPreview(
                      customer.NextAction,
                      "nextAction",
                      customer
                    )}
                    placement="top"
                    arrow
                    enterDelay={300}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "transparent",
                          boxShadow: "none",
                          maxWidth: "none",
                          "& .MuiTooltip-arrow": {
                            color: "transparent",
                          },
                        },
                      },
                    }}
                  >
                    <Chip
                      label={`${getWorkItemTypeName(
                        customer.NextAction.ActionType
                      )} ${formatTime(customer.NextAction.ActionDate)}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 22,
                        fontSize: "0.65rem",
                        borderColor: getWorkItemColor(
                          customer.NextAction.ActionType
                        ),
                        color: getWorkItemColor(customer.NextAction.ActionType),
                        bgcolor: "white",
                        fontWeight: 600,
                        "& .MuiChip-label": {
                          px: 0.75,
                        },
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.6rem",
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    Ingen planerad
                  </Typography>
                )}
              </Box>
            </Box>

            <Stack
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
              sx={{
                alignItems: "center",
                p: 1,
              }}
            >
              <Checkbox
                checked={isSelected}
                onClick={(event) =>
                  handleCheckboxClick(event, customer.Id, index)
                }
                sx={{ p: 0 }}
              />

              {/* Main content area with flexible layout */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  pt: 2,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {/* Name and location - integrated */}
                <Box sx={{ minWidth: 250, flex: 1 }}>
                  <Typography variant="body1" fontWeight="600" noWrap>
                    {customer.Name}
                  </Typography>
                  {customer.PostalCity && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.25,
                      }}
                    >
                      <LocationIcon
                        sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {customer.PostalCity}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Stack>
          </Box>
        );
      })}

      {!onContextMenu && (
        <CustomerContextMenu
          contextMenu={contextMenu}
          onClose={handleCloseContextMenu}
          onAction={handleMenuAction}
        />
      )}
    </Box>
  );
};

export default CustomerCardRow;
